/**
 * Tela Rede: mapa com relevo, rede do cadastro colorida pela pressão (NBR 12218) e linha do tempo — Nexora Grid.
 *
 * Criado em: 17/10/2026 · Última modificação: 26/10/2026 · Versão: 1.3.0
 *
 * US-404: a prova S1 dentro da aplicação, lendo a API em vez de arquivos:
 * - rede da importação vigente e equipamentos do cadastro; cotas pelo relevo;
 * - simulação de 24 h no navegador (mesmo modelo do SCADA 3D, gerado do cadastro);
 * - mapa gratuito (OpenStreetMap + Terrain Tiles, P1) com terreno 3D e sombreamento;
 * - clique no equipamento: ficha com atalhos para o SCADA 3D, o CCO e o cadastro; `?foco=ID` voa até ele.
 * - GUS-1105: simulação de 24 h no servidor (EPANET 2.2) com a situação do modelo por setor.
 * Pressões não calibradas: o mapa mostra o comportamento da rede, não um valor para operar.
 * v1.3 (US-3602): camada de edificações de bases abertas extrudadas pela altura (cor por uso ou por setor), comércios e
 * serviços como pontos com nome, ficha ao clicar, liga/desliga e a atribuição das fontes no mapa.
 */
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { TIPOS } from "../cadastro/tipos";
import { useSessao } from "../sessao";
import { gerarInp } from "../scada3d/modelo";
import { aplicarCotas, redeDaApi, type Equipamento, type FeicaoAtivo, type NoApi, type Rede as RedeDados, type TrechoApi } from "../scada3d/rede";
import { carregarRelevo } from "../scada3d/relevo";
import { NBR_MAX, NBR_MIN, simular, type Simulacao } from "../scada3d/simulacao";
import { SimulacaoServidor } from "./SimulacaoServidor";
import { carregarEdificacoes, COR_USO, NOME_USO, type ColecaoEdificacoes, type PropsEdificacao } from "../edificacoes";

const TERRARIUM = "https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png";
const COR_EQUIPAMENTO: Record<string, string> = {
  reservatorio: "#e5e7eb", bomba: "#c084fc", poco_profundo: "#a78bfa", macromedidor: "#22d3ee", vrp: "#facc15",
  registro_fechado: "#000000", elevatoria: "#c084fc", ventosa: "#94a3b8", hidrante: "#fb7185", ponto_pressao: "#38bdf8",
};
const FAIXAS = [
  { cor: "#e5383b", texto: `< ${NBR_MIN} mca — abaixo do mínimo` }, { cor: "#4f8fe6", texto: `${NBR_MIN}–25 mca` },
  { cor: "#3fbf7f", texto: `25–${NBR_MAX} mca` }, { cor: "#f28c28", texto: `> ${NBR_MAX} mca — acima do máximo` },
];
const corPressao = (p: number | undefined) => (p === undefined ? "#6b7280" : p < NBR_MIN ? "#e5383b" : p > NBR_MAX ? "#f28c28" : p < 25 ? "#4f8fe6" : "#3fbf7f");
const corSetor = (setor: string) => {
  let hash = 0;
  for (const caractere of setor) hash = (hash * 31 + caractere.charCodeAt(0)) | 0;
  return `hsl(${Math.abs(hash) % 360} 72% 58%)`;
};

export function Rede() {
  const { empresa } = useSessao();
  const [parametros] = useSearchParams();
  const caixa = useRef<HTMLDivElement>(null);
  const mapa = useRef<maplibregl.Map | null>(null);
  const redeEnquadrada = useRef<RedeDados | null>(null);
  const listaEquipamentos = useRef<Equipamento[]>([]);
  const [rede, setRede] = useState<RedeDados | null>(null);
  const [sim, setSim] = useState<Simulacao | null>(null);
  const [estado, setEstado] = useState("lendo a rede do cadastro…");
  const [hora, setHora] = useState(new Date().getHours());
  const [tocando, setTocando] = useState(false);
  const [selecionado, setSelecionado] = useState<Equipamento | null>(null);
  const [mapaPronto, setMapaPronto] = useState(false);
  const [origens, setOrigens] = useState<{ id: string; rede: string; arquivo: string; municipio: string | null; vigente: boolean }[]>([]);
  const [nomesMunicipios, setNomesMunicipios] = useState<Record<string, string>>({});
  const [origem, setOrigem] = useState("");
  const [edificacoes, setEdificacoes] = useState<ColecaoEdificacoes | null>(null);
  const [mostrarEdificacoes, setMostrarEdificacoes] = useState(true);
  const [corEdificacoes, setCorEdificacoes] = useState<"uso" | "setor">("uso");
  const [edificacao, setEdificacao] = useState<PropsEdificacao | null>(null);
  const [divisas, setDivisas] = useState<{ type: "FeatureCollection"; features: { type: "Feature"; geometry: { type: "LineString"; coordinates: number[][] }; properties: Record<string, unknown> }[] }>({ type: "FeatureCollection", features: [] });

  useEffect(() => {
    if (!empresa) return;
    Promise.all([
      api<typeof origens>(`/v1/ativos/dados-reais?empresa=${empresa}`),
      api<{ municipios: { codigo: string; nome: string; cadastrado: boolean }[] }>(`/v1/municipios?empresa=${empresa}`),
    ]).then(([lista, cadastro]) => {
      setOrigens(lista.filter((i) => i.vigente && i.municipio));
      setNomesMunicipios(Object.fromEntries(cadastro.municipios.filter((m) => m.cadastrado).map((m) => [m.codigo, m.nome])));
    }).catch(() => { setOrigens([]); setNomesMunicipios({}); });
  }, [empresa]);

  // US-3602: edificações do município da rede escolhida (ou do primeiro cadastrado)
  useEffect(() => {
    const municipio = origens.find((i) => i.id === origem)?.municipio ?? Object.keys(nomesMunicipios)[0];
    if (!empresa || !municipio) return;
    carregarEdificacoes(empresa, municipio).then(setEdificacoes).catch(() => setEdificacoes(null));
  }, [empresa, origem, origens, nomesMunicipios]);

  useEffect(() => {
    const m = mapa.current;
    if (!m || !mapaPronto || !edificacoes) return;
    const dados = { ...edificacoes, features: edificacoes.features.map((f) => ({ ...f, properties: { ...f.properties,
      cor: corEdificacoes === "uso" ? COR_USO[f.properties.uso] ?? COR_USO.desconhecido : corSetor(f.properties.setor ?? "—") } })) };
    const fonte = m.getSource("edificacoes") as maplibregl.GeoJSONSource | undefined;
    if (fonte) fonte.setData(dados as never);
    else {
      m.addSource("edificacoes", { type: "geojson", data: dados as never, attribution: edificacoes.atribuicao.join(" · ") });
      m.addLayer({ id: "edificacoes", type: "fill-extrusion", source: "edificacoes", filter: ["==", ["get", "tipo"], "edificacao"],
        paint: { "fill-extrusion-color": ["get", "cor"], "fill-extrusion-height": ["coalesce", ["get", "altura_m"], 3], "fill-extrusion-opacity": 0.85 } },
        m.getLayer("trechos") ? "trechos" : undefined);
      m.addLayer({ id: "comercios", type: "circle", source: "edificacoes", filter: ["==", ["get", "tipo"], "ponto"], minzoom: 14,
        paint: { "circle-color": ["get", "cor"], "circle-radius": 4, "circle-stroke-color": "#111827", "circle-stroke-width": 1 } });
      m.addLayer({ id: "comercios-nome", type: "symbol", source: "edificacoes", filter: ["==", ["get", "tipo"], "ponto"], minzoom: 16.5,
        layout: { "text-field": ["get", "nome"], "text-size": 10, "text-offset": [0, 1], "text-anchor": "top" },
        paint: { "text-color": "#fde68a", "text-halo-color": "#111827", "text-halo-width": 1 } });
      for (const camada of ["edificacoes", "comercios"]) {
        m.on("click", camada, (evento: maplibregl.MapLayerMouseEvent) => {
          const p = evento.features?.[0]?.properties as PropsEdificacao | undefined;
          if (p) setEdificacao({ ...p, altura_estimada: String(p.altura_estimada) === "true" });
        });
      }
    }
    for (const camada of ["edificacoes", "comercios", "comercios-nome"]) m.setLayoutProperty(camada, "visibility", mostrarEdificacoes ? "visible" : "none");
  }, [mapaPronto, edificacoes, corEdificacoes, mostrarEdificacoes]);

  // dados: importação vigente + cadastro → cotas pelo relevo → simulação
  useEffect(() => {
    if (!empresa) return;
    let ativo = true;
    (async () => {
      try {
        const vigentes = await api<{ id: string; vigente: boolean }[]>(`/v1/ativos/importacoes?empresa=${empresa}`);
        const vigente = vigentes.find((i) => i.vigente);
        // US-2306: sem DXF vigente, abre a primeira rede EPANET (dados reais) associada a um município
        if (!vigente && !origem && origens.length) { setOrigem(origens[0].id); return; }
        const selecionada = origens.find((i) => i.id === origem);
        if (!vigente && !selecionada) throw new Error("A operadora ainda não tem rede importada: importe o EPANET/KML ou o DWG/DXF em Cadastro › Importar.");
        if (selecionada) setSim(null);
        const [redeApi, geo] = selecionada
          ? await Promise.all([api<{ nos: NoApi[]; trechos: TrechoApi[] }>(`/v1/ativos/dados-reais/${selecionada.id}/rede`),
            api<typeof divisas>(`/v1/ativos/dados-reais/${selecionada.id}/divisas`),
            api<{ features: FeicaoAtivo[] }>(`/v1/ativos/geojson?empresa=${empresa}`)]).then(([r, d, g]) => { setDivisas(d); return [r, g] as const; })
          : await Promise.all([api<{ nos: NoApi[]; trechos: TrechoApi[] }>(`/v1/ativos/importacoes/${vigente!.id}/rede`),
            api<{ features: FeicaoAtivo[] }>(`/v1/ativos/geojson?empresa=${empresa}`)]).then(([r, g]) => { setDivisas({ type: "FeatureCollection", features: [] }); return [r, g] as const; });
        const r = redeDaApi(redeApi.nos, redeApi.trechos, geo.features);
        if (!ativo) return;
        setRede(r);
        setEstado("carregando o relevo…");
        const relevo = await carregarRelevo(r);
        aplicarCotas(r, relevo.cota);
        if (!ativo) return;
        if (selecionada) {
          setSim(null);
          setEstado(`EPANET ${selecionada.rede} · ${r.trechos.length} trechos · ${r.nos.size} nós · setores do modelo (sem simulação)`);
        } else {
          setEstado("simulando 24 h…");
          const s = await simular(gerarInp(r, relevo.cota));
          if (!ativo) return;
          setSim(s);
          setEstado(`DXF vigente · ${r.trechos.length} trechos · ${r.nos.size} nós · simulação ${s.tempoMs.toFixed(0)} ms · pressões não calibradas`);
        }
      } catch (e) {
        if (ativo) setEstado(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { ativo = false; };
  }, [empresa, origem, origens]);

  // mapa (uma vez)
  useEffect(() => {
    if (!caixa.current) return;
    const m = new maplibregl.Map({
      container: caixa.current, center: [-53.1318, -18.5436], zoom: 14, // Costa Rica/MS (única cidade da POC); a rede reenquadra ao carregar pitch: 55, bearing: -15, maxPitch: 80,
      attributionControl: { compact: true },
      style: {
        version: 8,
        sources: {
          osm: { type: "raster", tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"], tileSize: 256, attribution: "© OpenStreetMap" },
          relevo: { type: "raster-dem", tiles: [TERRARIUM], encoding: "terrarium", tileSize: 256, maxzoom: 14 },
          sombra: { type: "raster-dem", tiles: [TERRARIUM], encoding: "terrarium", tileSize: 256, maxzoom: 14 },
        },
        layers: [
          { id: "osm", type: "raster", source: "osm", paint: { "raster-saturation": -0.85, "raster-brightness-max": 0.55 } },
          { id: "sombra", type: "hillshade", source: "sombra", paint: { "hillshade-exaggeration": 0.5 } },
        ],
        terrain: { source: "relevo", exaggeration: 2 },
      },
    });
    m.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), "top-right");
    m.on("load", () => setMapaPronto(true));
    mapa.current = m;
    return () => m.remove();
  }, []);

  const trechosGeo = useMemo(() => {
    if (!rede) return null;
    const pressoes = sim?.pressoes[hora % sim.horas];
    return { type: "FeatureCollection" as const, features: rede.trechos.flatMap((t) => {
      const a = rede.nos.get(t.de), b = rede.nos.get(t.para);
      if (!a || !b) return [];
      const ps = [t.de, t.para].map((id) => pressoes?.get(id)).filter((p): p is number => p !== undefined);
      const media = ps.length ? ps.reduce((s, p) => s + p, 0) / ps.length : undefined;
      return [{ type: "Feature" as const, geometry: { type: "LineString" as const, coordinates: [[a.lon, a.lat], [b.lon, b.lat]] },
        properties: { id: t.id, dn: t.dn, cor: origem ? corSetor(t.setor) : corPressao(media), setor: t.setor } }];
    }) };
  }, [rede, sim, hora, origem]);

  const contagem = useMemo(() => {
    const p = sim?.pressoes[hora % (sim?.horas ?? 24)];
    let abaixo = 0, acima = 0, total = 0;
    p?.forEach((v) => { total++; if (v < NBR_MIN) abaixo++; if (v > NBR_MAX) acima++; });
    return { abaixo, acima, total };
  }, [sim, hora]);

  // camadas da rede e dos equipamentos
  useEffect(() => {
    const m = mapa.current;
    if (!m || !mapaPronto || !rede || !trechosGeo) return;
    const fonteDivisas = m.getSource("divisas") as maplibregl.GeoJSONSource | undefined;
    if (fonteDivisas) fonteDivisas.setData(divisas);
    const fonte = m.getSource("trechos") as maplibregl.GeoJSONSource | undefined;
    if (fonte) {
      fonte.setData(trechosGeo);
      // outra rede (troca de origem/empresa): reenquadra no município dela, não onde o mapa abriu
      if (redeEnquadrada.current !== rede) {
        redeEnquadrada.current = rede;
        const lista = rede.equipamentos.filter((e) => e.tipo !== "registro");
        listaEquipamentos.current = lista;
        (m.getSource("equipamentos") as maplibregl.GeoJSONSource | undefined)?.setData({ type: "FeatureCollection", features: lista.map((e, i) => ({
          type: "Feature" as const, geometry: { type: "Point" as const, coordinates: [e.lon, e.lat] },
          properties: { indice: i, id: e.id, tipo: e.tipo, cor: COR_EQUIPAMENTO[e.tipo] ?? "#ffffff" } })) } as never);
        const limites = new maplibregl.LngLatBounds();
        rede.nos.forEach((n) => limites.extend([n.lon, n.lat]));
        if (!limites.isEmpty()) m.fitBounds(limites, { padding: 60, pitch: 50, duration: 0 });
      }
      return;
    }
    redeEnquadrada.current = rede;
    m.addSource("trechos", { type: "geojson", data: trechosGeo });
    m.addLayer({ id: "trechos", type: "line", source: "trechos",
      paint: { "line-color": ["get", "cor"], "line-width": ["interpolate", ["linear"], ["get", "dn"], 50, 1.5, 300, 5] } });
    if (!fonteDivisas) m.addSource("divisas", { type: "geojson", data: divisas });
    if (!m.getLayer("divisas")) m.addLayer({ id: "divisas", type: "line", source: "divisas", paint: { "line-color": "#fbbf24", "line-width": 3, "line-dasharray": [2, 1] } });
    const equipamentos = { type: "FeatureCollection" as const, features: rede.equipamentos.filter((e) => e.tipo !== "registro").map((e, i) => ({
      type: "Feature" as const, geometry: { type: "Point" as const, coordinates: [e.lon, e.lat] },
      properties: { indice: i, id: e.id, tipo: e.tipo, cor: COR_EQUIPAMENTO[e.tipo] ?? "#ffffff" } })) };
    const lista = rede.equipamentos.filter((e) => e.tipo !== "registro");
    listaEquipamentos.current = lista;
    m.addSource("equipamentos", { type: "geojson", data: equipamentos });
    m.addLayer({ id: "equipamentos", type: "circle", source: "equipamentos",
      paint: { "circle-color": ["get", "cor"], "circle-radius": 7, "circle-stroke-color": "#111827", "circle-stroke-width": 2 } });
    m.addLayer({ id: "rotulos", type: "symbol", source: "equipamentos", minzoom: 15,
      layout: { "text-field": ["get", "id"], "text-size": 11, "text-offset": [0, 1.2], "text-anchor": "top" },
      paint: { "text-color": "#e6e9ee", "text-halo-color": "#111827", "text-halo-width": 1.2 } });
    m.on("click", "equipamentos", (evento: maplibregl.MapLayerMouseEvent) => {
      const f = evento.features?.[0];
      if (f) setSelecionado(listaEquipamentos.current[Number(f.properties?.indice)]);
    });
    m.on("mouseenter", "equipamentos", () => { m.getCanvas().style.cursor = "pointer"; });
    m.on("mouseleave", "equipamentos", () => { m.getCanvas().style.cursor = ""; });
    const foco = parametros.get("foco");
    const alvo = foco ? rede.equipamentos.find((e) => e.id === foco) : undefined;
    if (alvo) {
      setSelecionado(alvo);
      m.flyTo({ center: [alvo.lon, alvo.lat], zoom: 17.5, pitch: 60, duration: 2000 });
    } else {
      const limites = new maplibregl.LngLatBounds();
      rede.nos.forEach((n) => limites.extend([n.lon, n.lat]));
      m.fitBounds(limites, { padding: 60, pitch: 50, duration: 0 });
    }
  }, [mapaPronto, rede, trechosGeo, parametros, divisas]);

  useEffect(() => {
    if (!tocando) return;
    const relogio = window.setInterval(() => setHora((h) => (h + 1) % 24), 1500);
    return () => window.clearInterval(relogio);
  }, [tocando]);

  return (
    <section className="tela-rede" aria-label="Rede">
      <div ref={caixa} className="mapa-rede" role="application" aria-label="Mapa da rede com relevo" />
      <div className="nx-cartao painel-rede">
        <h3>{origem ? "Rede · EPANET por setor" : `Rede · pressão às ${String(hora).padStart(2, "0")}:00`}</h3>
        <label>Origem da rede <select aria-label="Origem da rede" value={origem} onChange={(e) => setOrigem(e.target.value)}>
          <option value="">Desenho vigente (DXF)</option>{origens.map((i) => <option key={i.id} value={i.id}>EPANET · {nomesMunicipios[i.municipio!] ?? i.municipio} · {i.rede} · {i.arquivo}</option>)}
        </select></label>
        <p className="nx-suave" id="estado-rede">{estado}</p>
        {!origem && <ul className="faixas-rede">{FAIXAS.map((f) => <li key={f.cor}><span className="cor" style={{ background: f.cor }} />{f.texto}</li>)}</ul>}
        {origem && <p>Trechos coloridos pelo setor informado no modelo EPANET. Pressão e telemetria não associadas.</p>}
        {!origem && sim && <p>NBR 12218: <b className="nx-valor">{contagem.abaixo}</b> nós abaixo de {NBR_MIN} mca · <b className="nx-valor">{contagem.acima}</b> acima de {NBR_MAX} mca (de {contagem.total})</p>}
        {!origem && <div className="linha-hora">
          <button type="button" className="nx-botao" onClick={() => setTocando(!tocando)} aria-label={tocando ? "Pausar" : "Tocar"}>{tocando ? "❚❚" : "▶"}</button>
          <input type="range" min={0} max={23} value={hora} onChange={(e) => setHora(Number(e.target.value))} aria-label="Hora da simulação" />
        </div>}
        {edificacoes && edificacoes.features.length > 0 && (
          <div className="camada-edificacoes" role="group" aria-label="Camada de edificações" style={{ display: "grid", gap: 4 }}>
            <label><input type="checkbox" checked={mostrarEdificacoes} onChange={(e) => setMostrarEdificacoes(e.target.checked)} /> Edificações
              ({edificacoes.features.filter((f) => f.properties.tipo === "edificacao").length})</label>
            <label>cor por <select aria-label="Cor das edificações" value={corEdificacoes} onChange={(e) => setCorEdificacoes(e.target.value as "uso" | "setor")}>
              <option value="uso">uso</option><option value="setor">setor</option></select></label>
            {corEdificacoes === "uso" && <ul className="faixas-rede">{Object.entries(COR_USO).map(([uso, cor]) =>
              <li key={uso}><span className="cor" style={{ background: cor }} />{NOME_USO[uso]}</li>)}</ul>}
            <small className="nx-suave">{edificacoes.atribuicao.join(" · ")}</small>
          </div>
        )}
        {!origem && <SimulacaoServidor />}
      </div>
      {edificacao && (
        <div className="nx-cartao ficha-rede" role="region" aria-label="Edificação">
          <h3>{edificacao.nome || `Edificação ${NOME_USO[edificacao.uso] ?? edificacao.uso}`}</h3>
          <p>{edificacao.tipo === "ponto" ? `${NOME_USO[edificacao.uso]} (ponto)` : `${edificacao.altura_m ?? "—"} m · ${edificacao.pavimentos ?? "—"} pavimento(s)${edificacao.altura_estimada ? " (estimado)" : ""}`}
            {edificacao.area_m2 ? ` · ${Math.round(edificacao.area_m2)} m²` : ""}</p>
          <p className="nx-suave">uso {NOME_USO[edificacao.uso] ?? edificacao.uso} · setor {edificacao.setor ?? "—"} (aproximado) · nó {edificacao.no_epanet ?? "—"} · fonte {edificacao.fonte}</p>
          <button type="button" className="nx-botao" onClick={() => setEdificacao(null)}>fechar</button>
        </div>
      )}
      {selecionado && (
        <div className="nx-cartao ficha-rede" aria-live="polite">
          <h3>{selecionado.id || "(sem identificação)"}</h3>
          <p>{TIPOS[selecionado.tipo] ?? selecionado.tipo}{selecionado.detalhe ? ` · ${selecionado.detalhe}` : ""}</p>
          <p className="nx-suave">setor {selecionado.setor || "—"} · {selecionado.situacao}</p>
          <div className="linha-botoes">
            <Link className="nx-botao" to={`/scada3d?foco=${encodeURIComponent(selecionado.id)}`}>Abrir no 3D ›</Link>
            {selecionado.deviceId && <Link className="nx-botao" to={`/cco/equipamento/${selecionado.deviceId}`}>CCO ›</Link>}
            {selecionado.ativoId && <Link className="nx-botao" to={`/cadastro/${selecionado.ativoId}`}>Ficha técnica ›</Link>}
            <button type="button" className="nx-botao" onClick={() => setSelecionado(null)}>fechar</button>
          </div>
        </div>
      )}
    </section>
  );
}
